/* @flow */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { log } from '../../utils/jsUtils';
import TagReadonly from './TagReadonly';
import TagCustomersPicker from '../Feed/TagCustomersPicker';
import { showTagPicker, hideTagPicker } from '../../reduxStore/modules/tags/actions';
import { selectAllTags } from '../../reduxStore/modules/tags/selectors';
import { useTheme } from '@mui/material/styles';
import tagEditableStyles from './Styles/TagEditable.Style';

type TagType = {
  _id: string,
  name?: string,
  color?: string,
  TagName?: string,
  Name?: string,
  Color?: string
};

type Props = {
  customerId: string,
  customerName?: string,
  existingTags?: Array<TagType>,
  size?: 'small' | 'medium',
  variant?: 'default' | 'outlined',
  showAddNew?: boolean,
  onTagAdded?: Function,
  onTagRemoved?: Function,
  maxTags?: number,
  debug?: boolean,
  containerStyle?: Object,
  showOnlyAddButton?: boolean
};

const TagEditable = ({
  customerId,
  customerName,
  existingTags = [],
  size = 'medium',
  variant = 'default',
  showAddNew = true,
  onTagAdded,
  onTagRemoved,
  maxTags,
  debug = false,
  containerStyle = {},
  showOnlyAddButton = false
}: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const addButtonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [localIsOpen, setLocalIsOpen] = useState(false);
  
  // Get all tags from Redux for displaying in the picker
  const allTags = useSelector(selectAllTags);
  
  // Debug logging
  // useEffect(() => {
  //   if (debug) {
  //     log('[TagEditable] Rendering with:', { 
  //       customerId, 
  //       existingTagsCount: existingTags?.length || 0,
  //       showOnlyAddButton
  //     });
  //   }
  // }, [customerId, existingTags, debug, showOnlyAddButton]);
  
  // Handle tag selected from the picker
  const handleTagSelected = useCallback((tag) => {
    if (onTagAdded) {
      onTagAdded(tag);
    }
    setLocalIsOpen(false);
    setAnchorEl(null);
  }, [onTagAdded]);
  
  // Handle tag removal
  const handleTagRemoved = useCallback((tag) => {
    if (debug) {
      log('[TagEditable] Tag removed:', tag);
    }
    
    if (onTagRemoved) {
      onTagRemoved(tag);
    }
  }, [onTagRemoved, debug]);
  
  // Handle clicking the "Add Tag" button
  const handleAddTagClick = useCallback((event) => {
    if (debug) {
      log('[TagEditable] Add tag clicked, customerId:', customerId);
    }
    
    if (!customerId) {
      log('[TagEditable] WARNING: No customerId provided for tag picker');
      return;
    }
    
    event.stopPropagation();
    event.preventDefault();
    
    setAnchorEl(event.currentTarget);
    setLocalIsOpen(true);
    
    // Also update Redux global state
    dispatch(showTagPicker(customerId));
  }, [customerId, dispatch, debug]);
  
  // Handle closing the tag picker
  const handleTagPickerClose = useCallback(() => {
    if (debug) {
      log('[TagEditable] Closing tag picker');
    }
    
    setLocalIsOpen(false);
    setAnchorEl(null);
    
    // Also update Redux global state
    dispatch(hideTagPicker());
  }, [dispatch, debug]);
  
  // Render the Add Tag button
  const renderAddTagButton = () => (
    <Box 
      ref={addButtonRef}
      onClick={handleAddTagClick}
      sx={tagEditableStyles.addTagButton(theme, size, showOnlyAddButton)}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
    >
      <AddIcon fontSize="small" />
    </Box>
  );
  
  // If we're only showing the Add Tag button
  if (showOnlyAddButton || existingTags.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'inline-flex',
          ...containerStyle
        }}
        onClick={(e) => {
          // Prevent clicks from reaching parent elements
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {showAddNew && renderAddTagButton()}
        
        {/* Tag picker popup */}
        {localIsOpen && customerId && (
          <TagCustomersPicker
            customerId={customerId}
            customerName={customerName}
            existingTags={existingTags}
            anchorEl={anchorEl}
            onClose={handleTagPickerClose}
            onTagPicked={handleTagSelected}
            debug={debug}
          />
        )}
      </Box>
    );
  }
  
  // Full component with existing tags and Add Tag button
  return (
    <Box 
      sx={{ 
        position: 'relative',
        display: 'flex', 
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        ...containerStyle
      }}
      onClick={(e) => {
        // Prevent clicks from reaching parent elements
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* List of existing tags */}
      {existingTags.map(tag => (
        <TagReadonly
          key={`tag-${tag._id}`}
          tag={tag}
          customerId={customerId}
          onRemove={handleTagRemoved}
          size={size}
          variant={variant}
        />
      ))}
      
      {/* Add tag button (if enabled) */}
      {showAddNew && (!maxTags || existingTags.length < maxTags) && renderAddTagButton()}
      
      {/* Tag picker popup */}
      {localIsOpen && customerId && (
        <TagCustomersPicker
          customerId={customerId}
          customerName={customerName}
          existingTags={existingTags}
          anchorEl={anchorEl}
          onClose={handleTagPickerClose}
          onTagPicked={handleTagSelected}
          debug={debug}
        />
      )}
    </Box>
  );
};

export default TagEditable;